// queue-row.js — CLIENT-SIDE shaping, raw FHIR -> the shapeQueueRow DTO
// (server/opd/view-adapter.mjs:778). Shared by queue.jsx AND reception.jsx —
// both render the same row shape, just over a different Encounter search
// (a doctor's own department vs. the front desk's whole board).
import { formatClinicTime } from '../clinic-time.js';
import { readQueueClass } from '../queue-engine.js';

const OPD_STATE = 'https://healther.io/opd/state';
const OPD_TOKEN = 'https://healther.io/opd/token';
const MRN_SYS = 'https://healther.io/opd/mrn';
const STATUS_TO_STATE = { planned: 'DRAFT', arrived: 'READY', triaged: 'READY', 'in-progress': 'IN_CONSULT', finished: 'COMPLETED', cancelled: 'DRAFT' };

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
const nameOf = (p) => { if (!p || !p.name || !p.name[0]) return 'Patient'; const n = p.name[0]; return n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' ') || 'Patient'; };
const ageOf = (bd) => { if (!bd) return null; const y = new Date().getUTCFullYear() - Number(String(bd).slice(0, 4)); return isNaN(y) ? null : y; };
const initials = (name) => String(name || '').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
const strip = (s) => String(s || '').replace(/<[^>]*>/g, '').trim();

export function shapeQueueRow(enc, patient, comps) {
  const stateExt = ext(enc, OPD_STATE);
  const state = (stateExt && stateExt.valueString) || STATUS_TO_STATE[enc.status] || 'DRAFT';
  const tokenExt = ext(enc, OPD_TOKEN);
  const mrn = patient && (patient.identifier || []).find((i) => i.system === MRN_SYS);
  const name = nameOf(patient);
  const start = enc.period && enc.period.start;
  const intake = (comps || []).filter((c) => c.type && c.type.text === 'intake-summary')[0];
  let preview = [];
  if (intake) {
    const cc = (intake.section || []).find((s) => s.code && s.code.coding && s.code.coding[0] && s.code.coding[0].code === 'chief_complaint');
    const t = cc && cc.text && strip(cc.text.div);
    if (t) preview = [t];
  }
  // Live Queue Engine (§1/§2/§5): the priority class + effective check-in instant
  // (queueOrder reads these) and the room a CALLED token was announced to
  // (Encounter.location[0].display — TokenDisplay/board show "→ Room X").
  const qc = readQueueClass(enc);
  const calledLoc = ((enc.location || [])[0] || {}).location || null;
  return {
    patientId: patient ? patient.id : null,
    token: tokenExt ? tokenExt.valueString : null,
    paid: null, referral: null, patientType: 'new', checkedInAt: null,
    id: enc.id, encounterId: enc.id,
    time: formatClinicTime(start), timeIso: start || null,
    mrn: mrn ? mrn.value : '',
    name, age: ageOf(patient && patient.birthDate),
    sex: (patient && patient.gender) ? patient.gender[0].toUpperCase() : '',
    initials: initials(name), phone: '',
    department: (enc.serviceProvider && enc.serviceProvider.display) || 'General Medicine',
    doctor: (enc.participant && enc.participant[0] && enc.participant[0].individual && enc.participant[0].individual.display) || '',
    state, rawState: state,
    queueClass: qc.class, effectiveSince: qc.effectiveSince,
    room: (calledLoc && calledLoc.display) || null,
    intake: state === 'DRAFT' ? 'not_started' : 'ready',
    preview, highlight: state === 'READY' || state === 'CALLED',
  };
}

// OPD SURFACE = AMBULATORY encounters only. The queue/reception/schedule boards
// are the OUT-patient world; an INPATIENT (class=IMP) or EMERGENCY (class=EMER)
// Encounter must NEVER become an OPD row — even when it shares the patient and
// falls in the same date window. This is the exact shape of the reported bug: a
// patient whose stay overlaps the board's window (an OPD consult that ESCALATED
// to an admission, or an admitted patient with OPD visits) had their IMP
// admission encounter surface in the "Yesterday · seen" OPD list; the row
// carried its OWN (correct) encounter id, but that id is an IMP id, so opening
// it at /consult was (correctly) diverted to the bed by the wrong-surface guard
// (medplum-encounter.js) — reading as "the OPD row opens the ward". The right
// place to fix is here, at the boundary that decides what IS an OPD row: an
// IMP/EMER encounter is not one. IPD/ED reachability is separate (the ward board
// / patient chart) per the owner product rule.
//
// We exclude PRECISELY the two classes that have a purpose-built non-OPD home —
// symmetric with medplum-encounter.js WRONG_SURFACE — and never a class-less or
// otherwise-classed encounter, so a legacy OPD row with no `class` still shows.
// This does NOT weaken the wrong-surface guard: a genuinely-IMP id typed/bookmarked
// at /consult/:id still diverts to the ward; we merely stop feeding IMP ids into
// OPD lists in the first place.
const NON_OPD_ENCOUNTER_CLASSES = new Set(['IMP', 'EMER']);
export function isOpdQueueEncounter(enc) {
  const code = String((enc && enc.class && enc.class.code) || '').toUpperCase();
  return !NON_OPD_ENCOUNTER_CLASSES.has(code);
}

// BUG (found 2026-07-19 during demo-video workflow testing): a cancelled
// Appointment cancels its linked Encounter too (medplum-booking.js cancelBooking
// sets Encounter.status='cancelled'), but neither queue.jsx, reception.jsx, nor
// schedule.jsx's Encounter search filtered by status — so the cancelled
// Encounter still came through this fan-in, shapeQueueRow's STATUS_TO_STATE
// mapped it to 'DRAFT' (cancelled has no dedicated queue state), and it
// rendered as an ordinary live row on the reception/queue board with no visual
// distinction from an active patient. No design guidance calls for a
// struck-through "cancelled" row anywhere in the queue/reception grammar, so
// the simplest honest fix is to drop cancelled/entered-in-error encounters
// entirely — symmetric with medplum-schedule.js's existing
// `status:not=cancelled` on the Appointment search (that one only covers
// Appointments; this covers the paired Encounter for every caller of
// groupEncounterBundle: queue.jsx, reception.jsx, schedule.jsx's join map, and
// the Yesterday/This-week history tabs).
const NON_LIVE_ENCOUNTER_STATUSES = new Set(['cancelled', 'entered-in-error']);
export function isLiveQueueEncounter(enc) {
  return !NON_LIVE_ENCOUNTER_STATUSES.has(String((enc && enc.status) || '').toLowerCase());
}

// Groups a today's-Encounters _include/_revinclude bundle into { patients,
// compsByEnc, encs (time-sorted) } — the shared fan-in both queue and
// reception searches need before calling shapeQueueRow per encounter. Non-OPD
// (IMP/EMER) encounters are dropped from `encs` here (see isOpdQueueEncounter),
// as are cancelled/entered-in-error ones (see isLiveQueueEncounter); their
// Patient/Composition includes are harmless if they linger in the maps.
export function groupEncounterBundle(bundle) {
  const patients = {}; const compsByEnc = {}; const encs = [];
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    if (r.resourceType === 'Patient') patients['Patient/' + r.id] = r;
    else if (r.resourceType === 'Composition') { const ref = r.encounter && r.encounter.reference; if (ref) (compsByEnc[ref] = compsByEnc[ref] || []).push(r); }
    else if (r.resourceType === 'Encounter') { if (isOpdQueueEncounter(r) && isLiveQueueEncounter(r)) encs.push(r); }
  }
  encs.sort((a, b) => String((a.period && a.period.start) || '').localeCompare(String((b.period && b.period.start) || '')));
  return { patients, compsByEnc, encs };
}
